/**
 * Affichage des threads WhatsApp embarqués dans la liste contacts (`threads`, `messages`, `stateCode`).
 */

export const WHATSAPP_THREAD_DISPLAY = Object.freeze({
  COMPLETED: 'completed',
  UNANSWERED: 'unanswered',
  ABANDONED: 'abandoned',
  IN_PROGRESS: 'in_progress',
  PENDING: 'pending',
  OPERATOR_REQUESTED: 'operator_requested',
  ERROR: 'error',
  UNKNOWN: 'unknown'
})

export const LEAD_AGENT_QUALIFICATION_PORTAL_CODE_NAME = 'LEAD_AGENT_QUALIFICATION'

/**
 * @param {number|string|null|undefined} stateCode
 * @returns {string} valeur de WHATSAPP_THREAD_DISPLAY
 */
export function mapApiThreadStateCodeToDisplayKey (stateCode) {
  if (stateCode == null || stateCode === '') {
    return WHATSAPP_THREAD_DISPLAY.UNKNOWN
  }
  const code = Number(stateCode)
  if (!Number.isFinite(code)) {
    return WHATSAPP_THREAD_DISPLAY.UNKNOWN
  }
  if (code === 100) {
    return WHATSAPP_THREAD_DISPLAY.PENDING
  }
  if (code === 200) {
    return WHATSAPP_THREAD_DISPLAY.IN_PROGRESS
  }
  if (code === 300 || code === 303) {
    return WHATSAPP_THREAD_DISPLAY.COMPLETED
  }
  if (code === 301) {
    return WHATSAPP_THREAD_DISPLAY.ABANDONED
  }
  if (code === 302) {
    return WHATSAPP_THREAD_DISPLAY.UNANSWERED
  }
  if (code === 400) {
    return WHATSAPP_THREAD_DISPLAY.COMPLETED
  }
  if (code >= 500 && code <= 599) {
    return WHATSAPP_THREAD_DISPLAY.ERROR
  }
  return WHATSAPP_THREAD_DISPLAY.UNKNOWN
}

/**
 * Clé i18n sous `contacts.` pour le libellé du badge liste / en-tête panneau.
 * @param {string} displayKey
 * @returns {string}
 */
export function getWhatsappThreadStateI18nKey (displayKey) {
  switch (displayKey) {
    case WHATSAPP_THREAD_DISPLAY.COMPLETED:
      return 'contacts.whatsappThreadStateCompleted'
    case WHATSAPP_THREAD_DISPLAY.UNANSWERED:
      return 'contacts.whatsappThreadStateUnanswered'
    case WHATSAPP_THREAD_DISPLAY.ABANDONED:
      return 'contacts.whatsappThreadStateAbandoned'
    case WHATSAPP_THREAD_DISPLAY.IN_PROGRESS:
      return 'contacts.whatsappThreadStateInProgress'
    case WHATSAPP_THREAD_DISPLAY.PENDING:
      return 'contacts.whatsappThreadStatePending'
    case WHATSAPP_THREAD_DISPLAY.OPERATOR_REQUESTED:
      return 'contacts.whatsappThreadStateOperatorRequested'
    case WHATSAPP_THREAD_DISPLAY.ERROR:
      return 'contacts.whatsappThreadStateError'
    case WHATSAPP_THREAD_DISPLAY.UNKNOWN:
    default:
      return 'contacts.whatsappThreadStateUnknown'
  }
}

/**
 * @param {{ threads?: unknown[] }} row
 * @returns {string} valeur de WHATSAPP_THREAD_DISPLAY
 */
export function getWhatsappThreadDisplayState (row) {
  const threads = row && Array.isArray(row.threads) ? row.threads : []
  if (threads.length === 0) {
    return WHATSAPP_THREAD_DISPLAY.UNKNOWN
  }
  const thread = pickLeadAgentQualificationThread(threads)
  if (!thread) {
    return WHATSAPP_THREAD_DISPLAY.UNKNOWN
  }
  return mapApiThreadStateCodeToDisplayKey(thread.stateCode)
}

/**
 * Thread de référence : `updatedAt` le plus récent ; ex aequo : `id` numérique décroissant.
 * @param {unknown[]} threads
 * @returns {object|null}
 */
export function pickPrimaryThread (threads) {
  if (!Array.isArray(threads) || threads.length === 0) {
    return null
  }
  const scored = threads.map((t) => {
    const updated = t && t.updatedAt != null ? new Date(t.updatedAt).getTime() : NaN
    const created = t && t.createdAt != null ? new Date(t.createdAt).getTime() : NaN
    const ts = Number.isFinite(updated) ? updated : (Number.isFinite(created) ? created : 0)
    const idNum = t && t.id != null ? Number(t.id) : 0
    return { thread: t, ts, idNum: Number.isFinite(idNum) ? idNum : 0 }
  })
  scored.sort((a, b) => {
    if (b.ts !== a.ts) {
      return b.ts - a.ts
    }
    return b.idNum - a.idNum
  })
  return scored[0].thread || null
}

/**
 * Thread à afficher dans les écrans contacts : portalCodeName `LEAD_AGENT_QUALIFICATION`.
 * Si plusieurs existent (cas anormal), garde le plus récent.
 * @param {unknown[]} threads
 * @returns {object|null}
 */
export function pickLeadAgentQualificationThread (threads) {
  if (!Array.isArray(threads) || threads.length === 0) {
    return null
  }
  const filtered = threads.filter((thread) => {
    return thread && thread.portalCodeName === LEAD_AGENT_QUALIFICATION_PORTAL_CODE_NAME
  })
  return pickPrimaryThread(filtered)
}

/**
 * @param {string} [locale]
 * @returns {(dateValue: string|number|Date) => string}
 */
export function createMessageTimeFormatter (locale) {
  const loc = locale || 'fr-FR'
  return (dateValue) => {
    if (!dateValue) {
      return ''
    }
    const parsedDate = new Date(dateValue)
    if (Number.isNaN(parsedDate.getTime())) {
      return ''
    }
    return parsedDate.toLocaleString(loc, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
}

/**
 * Messages API liste → format panneau (`from`, `body`, `time`).
 * `operator` → agent (bulle droite) ; `user` → interlocuteur (gauche).
 * @param {unknown[]} messages
 * @param {string} [locale]
 * @returns {{ from: 'agent'|'interlocutor', body: string, time: string }[]}
 */
export function mapWhatsappApiMessagesToUi (messages, locale) {
  const formatTime = createMessageTimeFormatter(locale)
  if (!Array.isArray(messages)) {
    return []
  }
  const list = [...messages]
    .filter((m) => m && (m.content != null || m.body != null))
    .sort((a, b) => {
      const ta = new Date(a.createdAt || a.updatedAt || 0).getTime()
      const tb = new Date(b.createdAt || b.updatedAt || 0).getTime()
      return ta - tb
    })
  return list.map((m) => {
    const rawUser = m.user != null ? String(m.user).toLowerCase() : ''
    const from = rawUser === 'operator' || rawUser === 'system' ? 'agent' : 'interlocutor'
    const body = m.content != null ? String(m.content) : (m.body != null ? String(m.body) : '')
    const time = formatTime(m.createdAt || m.updatedAt)
    return { from, body, time }
  }).filter((m) => m.body.length > 0)
}

/**
 * @type {readonly string[]}
 * Ordre d'affichage (clés JSON) — achat, puis location, pour couvrir les jeux de champs métiers.
 */
const TRANSCRIPT_JSON_FIELD_ORDER = Object.freeze([
  'contact_full_name',
  'project_type',
  'search_location',
  'property_type',
  'budget',
  'criteria',
  'previous_visits_feedback',
  'project_deadline',
  'financing_status',
  'is_current_owner',
  'needs_property_estimation',
  'contact_availability',
  'occupant_count',
  'area',
  'room_count',
  'tenant_status',
  'employment_status',
  'monthly_net_income',
  'has_guarantor'
])

/**
 * @type {Readonly<Record<string, string>>}
 * Suffixe de clé i18n (namespace `contacts.` géré par l'appelant) : ex. `whatsappTranscriptFieldBudget`.
 */
const TRANSCRIPT_FIELD_LABEL_KEY_BY_JSON_KEY = Object.freeze({
  contact_full_name: 'whatsappTranscriptFieldContactFullName',
  project_type: 'whatsappTranscriptFieldProjectType',
  search_location: 'whatsappTranscriptFieldSearchLocation',
  property_type: 'whatsappTranscriptFieldPropertyType',
  budget: 'whatsappTranscriptFieldBudget',
  criteria: 'whatsappTranscriptFieldCriteria',
  previous_visits_feedback: 'whatsappTranscriptFieldPreviousVisitsFeedback',
  project_deadline: 'whatsappTranscriptFieldProjectDeadline',
  financing_status: 'whatsappTranscriptFieldFinancingStatus',
  is_current_owner: 'whatsappTranscriptFieldIsCurrentOwner',
  needs_property_estimation: 'whatsappTranscriptFieldNeedsPropertyEstimation',
  contact_availability: 'whatsappTranscriptFieldContactAvailability',
  occupant_count: 'whatsappTranscriptFieldOccupantCount',
  area: 'whatsappTranscriptFieldArea',
  room_count: 'whatsappTranscriptFieldRoomCount',
  tenant_status: 'whatsappTranscriptFieldTenantStatus',
  employment_status: 'whatsappTranscriptFieldEmploymentStatus',
  monthly_net_income: 'whatsappTranscriptFieldMonthlyNetIncome',
  has_guarantor: 'whatsappTranscriptFieldHasGuarantor'
})

const TRANSCRIPT_CURRENCY_EUR_KEYS = new Set(['budget', 'monthly_net_income'])

/**
 * @param {object|null|undefined} data
 * @returns {object|null}
 */
export function pickTranscriptJsonForConversationData (data) {
  if (!data || typeof data !== 'object') {
    return null
  }
  const direct = data.transcriptJson
  if (direct != null && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct
  }
  const th = pickLeadAgentQualificationThread(Array.isArray(data.threads) ? data.threads : [])
  if (th && th.transcriptJson != null && typeof th.transcriptJson === 'object' && !Array.isArray(th.transcriptJson)) {
    return th.transcriptJson
  }
  return null
}

/**
 * @param {string} key Clé JSON
 * @param {unknown} value
 * @param {function} t — i18n `t` (clés `contacts.…`)
 * @param {string} [locale] `fr-FR`, `en-GB`…
 * @returns {string}
 */
function formatTranscriptFieldValue (key, value, t, locale) {
  const loc = locale || 'fr-FR'
  if (typeof value === 'boolean') {
    return value
      ? t('contacts.whatsappMockValueYes')
      : t('contacts.whatsappMockValueNo')
  }
  if (typeof value === 'number') {
    if (TRANSCRIPT_CURRENCY_EUR_KEYS.has(key)) {
      if (!Number.isFinite(value)) {
        return String(value)
      }
      return value.toLocaleString(loc, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      })
    }
    if (key === 'area') {
      if (!Number.isFinite(value)) {
        return String(value)
      }
      return `${value.toLocaleString(loc)} m²`
    }
    return String(value)
  }
  return String(value)
}

/**
 * Indique si le thread LEAD_AGENT_QUALIFICATION d'une ligne contact est en erreur "non joignable" (stateCode 501).
 * @param {{ threads?: unknown[] }} row
 * @returns {boolean}
 */
export function rowWhatsappThreadIsUnreachable (row) {
  const thread = pickLeadAgentQualificationThread(Array.isArray(row && row.threads) ? row.threads : [])
  if (!thread) {
    return false
  }
  return Number(thread.stateCode) === 501
}

/**
 * Statut inconnu : pas de lien vers la conversation (affichage « Conversation impossible »).
 * @param {{ threads?: unknown[] }} row
 * @returns {boolean}
 */
export function rowWhatsappThreadIsConversationImpossible (row) {
  return getWhatsappThreadDisplayState(row) === WHATSAPP_THREAD_DISPLAY.UNKNOWN
}

/**
 * Extrait la valeur `TYPE_OFFRE` depuis le tableau `contactsData` d'une ligne contact.
 * Retourne `'L'` (location), `'V'` (vente) ou `null` si absent.
 * @param {unknown[]} contactsData
 * @returns {string|null}
 */
export function getTypeOffreFromContactsData (contactsData) {
  if (!Array.isArray(contactsData)) {
    return null
  }
  const entry = contactsData.find(
    (item) => item && item.data && item.data.code === 'TYPE_OFFRE'
  )
  return entry && typeof entry.value === 'string' ? entry.value : null
}

/**
 * Remplit `qualifiedFields` à partir de `transcriptJson` (présent sur le fil ou le détail).
 * N'affiche que les clés connues et seulement si la valeur est renseignée (le booléen `false` s'affiche).
 * @param {object|null|undefined} transcriptJson
 * @param {function} t — ex. `this.$t` ou i18n `t`
 * @param {string} [locale] Pour montants / nombres
 * @param {string|null} [typeOffre] Valeur TYPE_OFFRE (`'L'` = location, `'V'` = vente)
 * @returns {{ label: string, value: string }[]}
 */
export function buildQualifiedFieldsFromTranscriptJson (transcriptJson, t, locale, typeOffre) {
  if (transcriptJson == null || typeof transcriptJson !== 'object' || Array.isArray(transcriptJson)) {
    return []
  }
  const isRental = typeOffre === 'L'
  const out = []
  for (const key of TRANSCRIPT_JSON_FIELD_ORDER) {
    if (!Object.prototype.hasOwnProperty.call(transcriptJson, key)) {
      continue
    }
    const raw = transcriptJson[key]
    if (raw === null || raw === undefined) {
      continue
    }
    if (typeof raw === 'string' && raw.trim() === '') {
      continue
    }
    if (typeof raw === 'number' && Number.isNaN(raw)) {
      continue
    }
    let labelKeySuffix = TRANSCRIPT_FIELD_LABEL_KEY_BY_JSON_KEY[key]
    if (labelKeySuffix == null) {
      continue
    }
    if (key === 'budget' && isRental) {
      labelKeySuffix = 'whatsappTranscriptFieldBudgetRental'
    }
    const fullLabelKey = `contacts.${labelKeySuffix}`
    const label = t(fullLabelKey) || fullLabelKey
    out.push({
      label,
      value: formatTranscriptFieldValue(key, raw, t, locale)
    })
  }
  return out
}

/**
 * Construit le DTO panneau depuis un thread préchargé (liste contacts).
 * @param {{ thread: object, t: function, leadLabel?: string, propertyLabel?: string, locale?: string, typeOffre?: string|null }} opts
 * @returns {{ leadLabel: string, propertyLabel: string, qualificationLabel: string, isQualified: boolean, qualifiedFields: { label: string, value: string }[], messages: object[] }}
 */
export function buildDetailFromPrefetchedThread ({ thread, t, leadLabel = '', propertyLabel = '', locale, typeOffre = null }) {
  const displayKey = mapApiThreadStateCodeToDisplayKey(thread && thread.stateCode)
  const messages = mapWhatsappApiMessagesToUi(thread && thread.messages, locale)
  const qualificationLabel = t(getWhatsappThreadStateI18nKey(displayKey))
  const tj = thread && thread.transcriptJson
  const qualifiedFields = buildQualifiedFieldsFromTranscriptJson(
    tj && typeof tj === 'object' && !Array.isArray(tj) ? tj : null,
    t,
    locale,
    typeOffre
  )
  return {
    leadLabel: leadLabel != null ? String(leadLabel) : '',
    propertyLabel: propertyLabel != null ? String(propertyLabel) : '',
    qualificationLabel,
    isQualified: displayKey === WHATSAPP_THREAD_DISPLAY.COMPLETED,
    qualifiedFields,
    messages
  }
}
